import { css } from "@emotion/react";
import axios, { AxiosResponse } from "axios";
import { Button } from "component/button";
import { MouseEventHandler, useRef, useState, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import { usePhotoBlobs, useDisplayedPhoto } from "globalState";
import { SECONDARY_COLOR } from "style";
import { PHOTO_RESOURCE_BASE_URL } from "config"
import { SubContainer } from "./component/SubContainer";
import { AiOutlineDownload, AiOutlineEdit, AiOutlineCalendar, AiOutlineCopy } from "react-icons/ai";
import { IconContext } from "react-icons";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PhotoWithIndex } from "type";

type Props = {
  photos?: PhotoWithIndex[];
  initialIndex?: number;
  onBackClick?: MouseEventHandler<HTMLButtonElement>;
}

export const PhotoDisplay = (props: Props) => {
  const location = useLocation();
  const [photoBlobs] = usePhotoBlobs();
  const [displayedPhotoState] = useDisplayedPhoto();

  // Combine props and location state to determine the context
  const contextPhotos = useMemo(() => {
    return props.photos ?? (location.state?.photos as PhotoWithIndex[]) ?? photoBlobs;
  }, [props.photos, location.state?.photos, photoBlobs]);

  const initialIndex = useMemo(() => {
    if (props.initialIndex !== undefined) return props.initialIndex;

    // Check location state first, then Recoil state
    const targetPhoto = location.state?.currentPhoto ?? displayedPhotoState;
    if (targetPhoto) {
      const idx = contextPhotos.findIndex((p: PhotoWithIndex) => p.filename === targetPhoto.filename);
      return idx !== -1 ? idx : 0;
    }
    return 0;
  }, [props.initialIndex, location.state?.currentPhoto, displayedPhotoState, contextPhotos]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const displayedPhoto = contextPhotos[currentIndex];

  const videoRef = useRef<HTMLVideoElement>(null);
  if (videoRef.current) {
    videoRef.current.load();
  }

  const [isCardVisible, setIsCardVisible] = useState(false);

  const imageSwipeHandlers = useSwipeable({
    onSwiped: (event) => {
      const isSwipeEnabled = props.photos !== undefined || location.state?.isSwipe;
      if (!isSwipeEnabled) return;

      if (event.dir === "Left") {
        setCurrentIndex((prev: number) => Math.min(contextPhotos.length - 1, prev + 1));
      }
      if (event.dir === "Right") {
        setCurrentIndex((prev: number) => Math.max(0, prev - 1));
      }
    },
    trackMouse: true,
  });

  if (!displayedPhoto) {
    return <div>写真が見つかりません。</div>;
  }

  const date = new Date(displayedPhoto.timestamp);
  const isVideo = displayedPhoto.type === 'video';

  const download = async () => {
    const res: AxiosResponse<Blob> = await axios.get(PHOTO_RESOURCE_BASE_URL + '/' + displayedPhoto.filename, {
      responseType: 'blob',
      headers: {
        "Cache-Control": "no-cache"
      }
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(res.data);
    link.setAttribute('download', displayedPhoto.filename);
    link.click();
  };

  const showCard = () => {
    setIsCardVisible(true);
  };

  const hideCard = () => {
    setIsCardVisible(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PHOTO_RESOURCE_BASE_URL + '/' + displayedPhoto.filename);
      toast.success('copy is successful!');
    } catch (err) {
      toast.error('copy fails');
    }
  };

  const element = isVideo ? (
    <video
      ref={videoRef}
      controls
      crossOrigin="anonymous"
      css={imgCss}
      playsInline
      key={displayedPhoto.filename} // Important to reset video when changed
    >
      <source src={PHOTO_RESOURCE_BASE_URL + '/' + displayedPhoto.filename + '#t=0.001'} type="video/mp4" />
    </video>
  ) : (
    <img
      src={PHOTO_RESOURCE_BASE_URL + '/' + displayedPhoto.filename}
      css={imgCss}
      alt="displayed"
      key={displayedPhoto.filename}
    />
  );

  return (
    <>
      <div
        css={mediaContainerCss}
        {...imageSwipeHandlers}
      >
        {element}
      </div>
      <div css={dateCss}>
        <AiOutlineCalendar />
        {` ${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`}
      </div>
      <div css={buttonContainerCss}>
        <IconContext.Provider value={{ size: "1.3rem" }}>
          <Button onClick={download}>
            <AiOutlineDownload />
            <span css={boldCss}> download</span>
          </Button>
          <Button onClick={showCard}>
            <AiOutlineEdit />
            <span css={boldCss}> edit</span>
          </Button>
          <Button onClick={copy}>
            <AiOutlineCopy />
            <span css={boldCss}> copy</span>
          </Button>
        </IconContext.Provider>
      </div>
      <SubContainer
        isVisible={isCardVisible}
        photo={displayedPhoto}
        onSwipeDown={hideCard}
      />
    </>
  );
};

const mediaContainerCss = css`
  display: flex;
  height: 100%;
`

const imgCss = css`
  display: block;
  margin: auto;
  object-fit: contain;
  max-height: 100%;
  max-width: 100%;
`

const dateCss = css`
  color: ${SECONDARY_COLOR};
  display: flex;
  justify-content: right;
  align-items: center;
`

const boldCss = css`
  font-weight: bold;
`

const buttonContainerCss = css`
  display: flex;
  justify-content: space-evenly;
  margin-top: 10px;
`